// [목적] /auth/callback/:provider — BE 302 redirect 착지점. refetchMe 후 next 로 navigate.
// [이유] fe-be-wire-up Wave 1 WC-FE-AUTH-03 + ADR-T5 + oauth-login WC-FE-CALLBACK-01 흡수.
//         BE callback 이 Set-Cookie 3종 발급 후 ${FE_BASE}/auth/callback/:provider?next=<nextRoute> 로 302.
//         FE 는 쿠키가 이미 있는 상태에서 GET /auth/me 를 refetch 하여 user 를 확인한 후 next 로 이동.
// [사이드 이팩트] ?error= 쿼리 파라미터가 있으면 /login?error=<code> 로 즉시 redirect.
//                 refetch 후 status==='guest' (hydrate 실패) 시 /login?error=auth.SESSION_HYDRATE_FAILED.
//                 무한루프 방지: GuestOnlyRoute 에서 loading/guest 는 children 통과 — 이 페이지는 GuestOnlyRoute 비적용.
//                 next가 /onboarding 으로 시작할 때 ?provider=<provider> 를 append — OnboardingPage 배지 동적화용.
// [수정 시 고려사항] provider 파라미터는 표시용도만 — 실제 provider 검증은 BE 에서 완료됨.
//                   next 파라미터가 없으면 기본 /로 이동 (온보딩 완료 사용자).

import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuthContext } from '../../providers/AuthProvider.js';
import { sanitizeNextPath } from '../../api/safeRedirect.js';
import { AUTH_COLORS } from './tokens.js';

/**
 * [목적] OAuth callback 착지 페이지 — refetchMe + next navigate
 * [이유] WC-FE-AUTH-03 + ADR-T5: BE redirect 착지 → /auth/me hydrate → next
 * [사이드 이팩트] 에러 쿼리 → /login?error=. hydrate 실패 → /login?error=auth.SESSION_HYDRATE_FAILED.
 *                 next가 /onboarding 으로 시작할 때 ?provider=<provider> 를 append.
 * [수정 시 고려사항] GuestOnlyRoute 비적용 (loading 중 redirect 금지)
 */
export function OAuthCallbackPage(): JSX.Element {
  const { provider } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { status, refetchMe } = useAuthContext();
  const ranRef = useRef(false);

  // [보안] open redirect 방어 — /auth/callback/:provider?next=//evil.com 직접 진입 차단 (BE 외 FE 경계)
  const next = sanitizeNextPath(searchParams.get('next'));
  const errorParam = searchParams.get('error');

  useEffect(() => {
    // 중복 실행 방지 (StrictMode double-invoke 대응)
    if (ranRef.current) return;
    ranRef.current = true;

    // 1. ?error 파라미터가 있으면 즉시 /login 으로
    if (errorParam) {
      navigate(`/login?error=${encodeURIComponent(errorParam)}`, { replace: true });
      return;
    }

    // 2. refetchMe 후 status 기반 navigate
    void (async () => {
      try {
        await refetchMe();
      } catch {
        // refetchMe 내부에서 이미 콘솔 경고 발생
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refetchMe 이후 status 변화 감지 → navigate
  useEffect(() => {
    // 에러 파라미터가 있으면 이미 navigate 처리됨
    if (errorParam) return;
    // 아직 로딩 중이면 대기
    if (status === 'loading') return;

    if (status === 'authenticated') {
      // next가 /onboarding으로 시작하면 provider query 추가 (OnboardingPage 배지 동적화용)
      const destination =
        next.startsWith('/onboarding') && provider
          ? `${next}${next.includes('?') ? '&' : '?'}provider=${encodeURIComponent(provider)}`
          : next;
      navigate(destination, { replace: true });
    } else {
      // hydrate 실패 (guest)
      navigate('/login?error=auth.SESSION_HYDRATE_FAILED', { replace: true });
    }
    // provider 는 표시용, navigate 변경에만 반응
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: AUTH_COLORS.brandCream,
        gap: '16px',
      }}
      role="status"
      aria-label="로그인 처리 중"
      aria-live="polite"
    >
      <CircularProgress size={40} aria-hidden="true" sx={{ color: AUTH_COLORS.brandGold }} />
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: AUTH_COLORS.textMid,
          letterSpacing: '-0.2px',
        }}
      >
        {provider ? `${getProviderLabel(provider)} 로그인 중...` : '로그인 중...'}
      </Typography>
    </Box>
  );
}

function getProviderLabel(provider: string): string {
  const labels: Record<string, string> = {
    kakao: '카카오',
    naver: '네이버',
    google: 'Google',
    apple: 'Apple',
  };
  return labels[provider] ?? provider;
}
